import { useEffect, useState } from 'react';
import { getIdCatCreditoPrecosecha, useCoopConfig } from '../components/content/coop-config.jsx';
import DebtCalculator from '../nuup/utils/debtCalculator.js';
import Api from '../api/global.service.js';
import getEstatus from '../nuup/utils/getStatusDebtColor.js';

/**
 * Fetches and computes creditos (anticipos) for a cycle and producer.
 * @param {Object} params
 * @param {string} [params.id_ciclo] - Cycle id (e.g. from localStorage ciclo_id)
 * @param {string} [params.productor_uuid] - Producer uuid
 * @returns {{ creditos: Object, blocking: boolean }}
 */
const useDeudaProductor = ({ id_ciclo, productor_uuid }) => {
  const [blocking, setBlocking] = useState(true);
  const [creditos, setCreditos] = useState(null);
  const coopConfig = useCoopConfig();

  useEffect(() => {
    if (!id_ciclo || !productor_uuid) {
      setBlocking(false);
      setCreditos(null);
      return;
    }
    setBlocking(true);
    const interesCoop = coopConfig.catCreditoPrecosecha()?.interes;
    Api.Acopio.search('anticipos', {
      filter: [
        { column: 'id_ciclo', operator: '=', value: id_ciclo },
        { column: 'productor_uuid', operator: '=', value: productor_uuid },
      ],
    })
      .then(response => {
        const updatedData = response.data.map(anticipo => {
          const total_anticipos_recuperados = anticipo.anticipo_pagos.reduce((acc, pg) => acc + +pg.cantidad_pago / 100, 0);

          // RETURN DATA WITH DEBT CALCULATION
          if (getIdCatCreditoPrecosecha() > 2) {
            const interes = anticipo.tasa_interes && +anticipo.tasa_interes >= 0 ? +anticipo.tasa_interes * 100 : +interesCoop;
            const debtCalc = new DebtCalculator({
              credito: anticipo,
              interestRate: interes,
              pagos: anticipo.anticipo_pagos,
              stringDate: null,
            });
            const calc = debtCalc.getDebt();
            const estatus = getEstatus(calc.debtToday.pendingCapital / 100);
            return {
              ...anticipo,
              total_anticipos_recuperados,
              total_saldo: calc.debtToday.pendingCapital / 100,
              total: +calc.debtToday.totalInterest / 100 + +anticipo.cantidad_anticipo / 100,
              total_interes: calc.debtToday.totalInterest / 100,
              estatus: estatus.label,
              estatus_color: estatus.color,
              tasa_interes: interes,
            };
          }
          // RETURN DATA WITH NO INTEREST RATE
          const estatus = getEstatus(+anticipo.cantidad_anticipo / 100 - +total_anticipos_recuperados);

          return {
            ...anticipo,
            total: +anticipo.cantidad_anticipo / 100,
            total_interes: 0,
            total_saldo: +anticipo.cantidad_anticipo / 100 - +total_anticipos_recuperados,
            estatus: estatus.label,
            estatus_color: estatus.color,
            comision_efectivo: 999,
          };
        });

        let _creditos_raw = updatedData.map(function(obj) {
          return {
            anticipo_id: obj.id,
            total_saldo: obj.total_saldo,
            fecha_ejecucion: obj.fecha_ejecucion,
          };
        });

        let _creditos = {
          creditos_no: updatedData.length,
          creditos_total:
            updatedData.reduce(function(tot, arr) {
              return tot + arr.total;
            }, 0) -
            updatedData.reduce(function(tot, arr) {
              return tot + arr.total_interes;
            }, 0),
          total_saldo: updatedData.reduce(function(tot, arr) {
            return tot + arr.total_saldo;
          }, 0),
          raw: _creditos_raw,
        };

        setCreditos(_creditos);
        setBlocking(false);
      })
      .catch(() => {
        setBlocking(false);
        setCreditos(null);
      });
  }, [id_ciclo, productor_uuid]);

  return { creditos, blocking };
};

export default useDeudaProductor;
